import { useState, useCallback, useMemo , useEffect, useRef } from "react";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import FullPageLoader from "@components/FullPageLoader";
import { UserContext, useToken } from "./contexts";
import LoginPage from "@pages/LoginPage";
import { checkRole } from "@utils/index";
import Fireworks from "@components/Fireworks";
import ChatwootWidget from "@components/ChatwootWidget";
import { useApi } from "@hooks/useApi";

import * as Sentry from '@sentry/react';
import ErrorComponent from "@components/ErrorComponent";
import { DEFAULT_FEATURE_FLAGS, updateFeatureFlags } from "@utils/featureFlagProvider";
import { syncBrowserPush, unsubscribeOnLogout } from "@utils/webPush";

const TEN_MIN_MS = 10 * 60 * 1000;
const FIVE_MIN_MS = 5 * 60 * 1000;
const NOT_IMPERSONATING = {
    isImpersonating: false,
    targetUser: null,
    targetLichessId: null,
    tokenExpiryTime: null,
    impersonationId: null,
};

export const UserProvider = ( { children } ) => {
    const fireworksRef = useRef();
    const queryClient = useQueryClient();
    const {
        token, clearToken, tokenLoading, localStorageAvailable, impersonationToken,
        setImpersonationToken, clearImpersonationToken, impersonationMetadata,
        setImpersonationMetadata,
    } = useToken();
    const api = useApi();
    const [isAdminView, setIsAdminView] = useState(false);

    // Impersonation is active while both the token and its metadata are stored;
    // clearImpersonationToken() drops both, which ends it
    const impersonationState = useMemo(() => (
        impersonationToken && impersonationMetadata
            ? {
                isImpersonating: true,
                targetUser: impersonationMetadata.targetUser || null,
                targetLichessId: impersonationMetadata.targetLichessId || null,
                tokenExpiryTime: impersonationMetadata.expiresAt ? new Date(impersonationMetadata.expiresAt) : null,
                impersonationId: impersonationMetadata.impersonationId || null,
            }
            : NOT_IMPERSONATING
    ), [impersonationToken, impersonationMetadata]);

    // React Query для получения предпочтений/настроек сайта (включая режим обслуживания)
    const { isLoading: prefsLoading, data: preferences, status: prefsStatus, error: prefsError } = useQuery({
        queryKey: ["preferences", token],
        queryFn: async () => {
            const data = await api.get("preferences");
            // Ожидаем unified preferences: { onboarding, notifications, siteSettings }
            return data.data; // поддерживаем оба варианта обёртки
        },
        enabled: Boolean(token),
        staleTime: TEN_MIN_MS,
        refetchInterval: TEN_MIN_MS,
        refetchOnWindowFocus: "always",
        gcTime: FIVE_MIN_MS,
    });

    // React Query для получения данных пользователя (ждём завершения загрузки настроек)
    const { data: userData, isLoading: userLoading, error: userError } = useQuery({
        queryKey: ['user-data', token],
        queryFn: async () => {
            const data = await api.get("user/");
            return data.data;
        },
        enabled: Boolean(token) && prefsStatus !== "pending",
        staleTime: TEN_MIN_MS,
        gcTime: FIVE_MIN_MS, // 5 минут
    });
    // Сообщаем пользователю, если localStorage недоступно
    useEffect(() => {
        if (!localStorageAvailable) {
            toast.error("Локальное хранилище (localStorage) недоступно. Установите поддерживаемый веб-браузер и перезагрузите страницу.");
        }
    }, [localStorageAvailable]);

    // Feature flags from preferences, over the defaults
    const storedFlags = preferences?.siteSettings?.featureFlags;
    const featureFlags = useMemo(
        () => (storedFlags ? { ...DEFAULT_FEATURE_FLAGS, ...storedFlags } : DEFAULT_FEATURE_FLAGS),
        [storedFlags],
    );
    // Mirror them into the OpenFeature provider (for Sentry)
    useEffect(() => {
        updateFeatureFlags(featureFlags);
    }, [featureFlags]);

    // Re-send this browser's push subscription on every start: covers subscription rotation and
    // account switches. Skipped while impersonating, so the moderator's browser stays theirs.
    const userId = userData?._id;
    const pushEnabled = featureFlags.pushNotifications;
    useEffect(() => {
        if (!userId || !pushEnabled || impersonationToken) return;
        syncBrowserPush(api).catch(err => console.warn("Push re-sync failed", err));
    }, [userId, pushEnabled, impersonationToken, api]);

    // Moderators land in the admin view each time their user data (re)loads;
    // they can switch it off from the header
    const [adminViewSeenFor, setAdminViewSeenFor] = useState(userData);
    if (userData !== adminViewSeenFor) {
        setAdminViewSeenFor(userData);
        if (checkRole(userData, "moderator")) setIsAdminView(true);
    }

    useEffect( () => {
        Sentry.setUser( userData ? { id: userData._id, email: userData.email, username: userData.lichessId } : null );
    }, [userData] );

    // Invalidate the query cache whenever impersonation starts or ends.
    // All queries are keyed by the main token, which doesn't change during
    // impersonation, so without this they'd keep serving the previous
    // identity's data. "auth-token" is excluded: it's the owner's session
    // bootstrap and must survive impersonation untouched.
    const prevImpersonationTokenRef = useRef(impersonationToken);
    useEffect(() => {
        if (prevImpersonationTokenRef.current === impersonationToken) {
            return;
        }
        prevImpersonationTokenRef.current = impersonationToken;
        queryClient.invalidateQueries({
            predicate: query => query.queryKey[0] !== "auth-token",
        });
    }, [impersonationToken, queryClient]);

    // Monitor impersonation token expiry
    useEffect(() => {
        if (!impersonationState.isImpersonating || !impersonationState.tokenExpiryTime) {
            return;
        }

        const interval = setInterval(() => {
            const now = new Date();
            const timeLeft = impersonationState.tokenExpiryTime - now;

            if (timeLeft <= 0) {
                clearImpersonationToken();
                toast.info("Сеанс персонификации истек");
                clearInterval(interval);
            }
        }, 1000); // Check every second

        return () => clearInterval(interval);
    }, [impersonationState.isImpersonating, impersonationState.tokenExpiryTime, clearImpersonationToken]);
    // React Query для получения уровней
    const { data: levels = [], error: levelsError } = useQuery({
        queryKey: ['levels', token],
        queryFn: async () => {
            const data = await api.get("levels/");
            if (!data.ok || !Array.isArray(data.data)) {
                throw new Error("Некорректный формат данных");
            }
            // Ранг звания определяется позицией в массиве (getLevelRank), а сервер
            // отдаёт уровни в порядке создания - уровень, добавленный между
            // существующими, иначе оказался бы последним
            return [...data.data].sort((a, b) => (a.rating ?? 0) - (b.rating ?? 0));
        },
        enabled: Boolean(token),
        staleTime: 10 * 60 * 1000, // 10 минут
        gcTime: 30 * 60 * 1000, // 30 минут
    });

    // react-query v5 dropped per-query onError, so failures are reported from an effect
    useEffect(() => {
        if (!prefsError) return;
        console.error("Ошибка загрузки настроек сайта:", prefsError);
        toast.error("Не удалось загрузить настройки сайта");
    }, [prefsError]);

    useEffect(() => {
        if (!levelsError) return;
        console.error("Ошибка загрузки уровней:", levelsError);
        toast.error("Не удалось загрузить уровни");
    }, [levelsError]);

    // Функция для выхода из системы
    const logout = useCallback(async () => {
        // Before the token goes, or the previous user keeps getting pushes in this browser
        await unsubscribeOnLogout(api);
        clearToken();
        queryClient.clear(); // Очищаем кеш React Query
        window.location.href = "/login/";
    }, [api, queryClient, clearToken]);

    // Start impersonation: store token and metadata
    const startImpersonation = useCallback((token, metadata) => {
        if (localStorageAvailable) {
            globalThis.localStorage.setItem("chess_impersonation_token", token);
            globalThis.localStorage.setItem("chess_impersonation_metadata", JSON.stringify(metadata));
        }
        setImpersonationToken(token);
        setImpersonationMetadata(metadata);
        toast.success(`Теперь персонифицируется ${metadata.targetLichessId}`);
    }, [localStorageAvailable, setImpersonationToken, setImpersonationMetadata]);

    // End impersonation: clear token and call API to invalidate
    const endImpersonation = useCallback(async () => {
        try {
            if (impersonationToken) {
                // Call API to end impersonation session on backend
                await api.post("auth/impersonate/end", {});
            }
        } catch (error) {
            console.info("Error ending impersonation:", error);
        } finally {
            clearImpersonationToken();
            toast.success("Персонификация завершена");
        }
    }, [impersonationToken, api, clearImpersonationToken]);

    // Мемоизация значения контекста для предотвращения ненужных ререндеров
    const contextValue = useMemo(
        () => ({
            token,
            userData,
            isAdminView,
            setIsAdminView,
            levels,
            logout,
            preferences,
            featureFlags,
            impersonationState,
            startImpersonation,
            endImpersonation,
            firework: () => {
                if ( fireworksRef.current ) {
                    const x = Math.random() * window.innerWidth;
                    const y = Math.random() * window.innerHeight;
                    fireworksRef.current.launch( x, y );
                }
            },
            launchMultipleFireworks: (count = 20) => {
                if (fireworksRef.current) {
                    for (let i = 0; i < count; i++) {
                        const x = Math.random() * window.innerWidth;
                        const y = (Math.random() * window.innerHeight) / 2;
                        fireworksRef.current.launch(x, y);
                    }
                }
            }
        }),
        [token, userData, isAdminView, levels, logout, preferences, featureFlags, impersonationState, startImpersonation, endImpersonation],
    );

    const isLoading = tokenLoading || userLoading || prefsLoading;
    const error = userError;

    if (error) {
        toast.error( `Ошибка загрузки данных пользователя: ${error.message}` );
        return <ErrorComponent message="Ошибка загрузки данных пользователя. Пожалуйста, попробуйте перезагрузить страницу." />;
    }
    if (isLoading) {
        return <FullPageLoader text="Загрузка..." />;
    }
    if (!localStorageAvailable) {
        return <FullPageLoader text="Ошибка: локальное хранилище недоступно. Установите поддерживаемый веб-браузер и перезагрузите страницу." />;
    }
    
    return (
        <UserContext.Provider value={contextValue}>
            { userData ? children : <LoginPage /> }
            <Fireworks ref={fireworksRef} />
            <ChatwootWidget />
        </UserContext.Provider>
    );
};
UserProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
